import { useState, useMemo, type FormEvent } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { ApiClientError } from "@victor/api-client";
import { Button } from "../../ui/Button";
import { Input } from "../../ui/Input";
import { Logo } from "../../ui/Logo";
import { api } from "../../lib/api";

/**
 * /invitation/accept?token=... · page d'atterrissage depuis le mail
 * d'invitation envoyé par le gérant (T1.5).
 *
 * 1. Récupère le token dans l'URL (sinon redirige sur /login)
 * 2. Demande un mot de passe initial (+ confirmation)
 * 3. POST /v1/auth/invitation/accept { token, password }
 * 4. Sur succès → écran de bienvenue + bouton vers /login
 *
 * Route publique : pas de guard. Le token est l'unique preuve d'identité.
 * Le TTL est de 7 jours côté API (vs 1h pour le reset password).
 */
export function InvitationAcceptPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const passwordStrength = useMemo(() => evaluateStrength(password), [password]);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (password.length < 12) {
      setError("Choisissez un mot de passe d'au moins 12 caractères.");
      return;
    }
    if (password !== confirm) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }

    setLoading(true);
    try {
      await api.auth.acceptInvitation({ token, password });
      setDone(true);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-white p-4">
      <AmbientBackground />

      <motion.div
        className="relative w-full max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="rounded-2xl bg-white p-8 shadow-2xl shadow-navy-800/10 ring-1 ring-navy-100 sm:p-10">
          <div className="flex flex-col items-center gap-3 text-center">
            <Logo size={48} withWordmark />
          </div>

          <AnimatePresence mode="wait">
            {done ? (
              <motion.div
                key="done"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
                className="mt-8 space-y-5 text-center"
              >
                <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200">
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h1 className="text-2xl font-bold tracking-tight text-navy-800">
                  Bienvenue dans Victor
                </h1>
                <p className="text-sm text-navy-600">
                  Votre compte est activé. Connectez-vous avec votre adresse
                  email et le mot de passe que vous venez de choisir.
                </p>
                <Button
                  onClick={() => navigate("/login", { replace: true })}
                  fullWidth
                  size="lg"
                >
                  Se connecter
                </Button>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                onSubmit={handleSubmit}
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
                className="mt-8 space-y-5"
              >
                <div>
                  <h1 className="text-2xl font-bold tracking-tight text-navy-800">
                    Activez votre compte
                  </h1>
                  <p className="mt-1 text-sm text-navy-500">
                    Choisissez un mot de passe long et unique. 12 caractères
                    minimum.
                  </p>
                </div>

                <div>
                  <Input
                    label="Mot de passe"
                    type="password"
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    minLength={12}
                    required
                    autoFocus
                  />
                  {password.length > 0 ? (
                    <PasswordStrengthBar strength={passwordStrength} />
                  ) : null}
                </div>

                <Input
                  label="Confirmer"
                  type="password"
                  name="confirm"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  minLength={12}
                  required
                />

                {error ? (
                  <div
                    role="alert"
                    className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700 ring-1 ring-red-200"
                  >
                    {error}
                  </div>
                ) : null}

                <Button type="submit" disabled={loading} fullWidth size="lg">
                  {loading ? "Activation…" : "Activer mon compte"}
                </Button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>

        <p className="mt-6 text-center text-xs text-navy-500">
          Ce lien est à usage unique et expire après 7 jours.
        </p>
      </motion.div>
    </div>
  );
}

type Strength = { score: 0 | 1 | 2 | 3 | 4; label: string };

function evaluateStrength(pw: string): Strength {
  let score = 0;
  if (pw.length >= 12) score++;
  if (pw.length >= 16) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/\d/.test(pw) && /[^A-Za-z0-9]/.test(pw)) score++;
  const s = Math.min(4, score) as 0 | 1 | 2 | 3 | 4;
  const labels = ["très faible", "faible", "moyen", "bon", "fort"] as const;
  return { score: s, label: labels[s] };
}

function PasswordStrengthBar({ strength }: { strength: Strength }) {
  const colors = [
    "bg-red-400",
    "bg-orange-400",
    "bg-amber-400",
    "bg-emerald-400",
    "bg-emerald-500",
  ];
  return (
    <div className="mt-2 flex items-center gap-2">
      <div className="flex flex-1 gap-1">
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className={`h-1 flex-1 rounded-full transition-colors ${
              i < strength.score ? colors[strength.score] : "bg-navy-100"
            }`}
          />
        ))}
      </div>
      <span className="text-xs font-semibold text-navy-500">
        {strength.label}
      </span>
    </div>
  );
}

function AmbientBackground() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 -z-10 overflow-hidden"
    >
      <div className="absolute -top-32 right-[-10%] h-[480px] w-[480px] rounded-full bg-accent-200/40 blur-3xl" />
      <div className="absolute top-40 left-[-10%] h-[420px] w-[420px] rounded-full bg-navy-100/40 blur-3xl" />
    </div>
  );
}

function formatError(err: unknown): string {
  if (err instanceof ApiClientError) {
    if (
      err.code === "invitation_token_invalid" ||
      err.code === "invitation_token_expired" ||
      err.code === "invitation_already_used"
    ) {
      return "Ce lien d'invitation n'est plus valide. Demandez à votre administrateur de vous renvoyer une invitation.";
    }
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return "Une erreur est survenue. Réessayez.";
}
